import React from "react";
import currencyPairCheck from "utilities/currencyPairCheck";
import { ArrowRight2, InfoCircle } from "iconsax-reactjs";

const formatRate = (value) =>
    Number(value || 0).toLocaleString(undefined, {
        maximumFractionDigits: 6,
    });

const OtcValueRow = ({ label, children, className = "" }) => (
    <div className={`flex items-start justify-between gap-6 ${className}`}>
        <span className="shrink-0 text-[#344054]">{label}</span>
        <div className="min-w-0 text-right font-medium text-[#667085]">{children}</div>
    </div>
);

const DeferredDetail = ({ onClick }) => (
    <div>
        <p>After approval</p>
        <button
            type="button"
            onClick={onClick}
            className="mt-1 inline-flex items-center gap-1 text-[#6F00FF]"
        >
            Add now
            <ArrowRight2 size={14} color="currentColor" />
        </button>
    </div>
);

const DashedSection = ({ children, bottom = true }) => (
    <div
        className="py-4"
        style={{
            borderTop: "1px dashed #DCE0F0",
            borderBottom: bottom ? "1px dashed #DCE0F0" : "none",
            borderLeft: "none",
            borderRight: "none",
        }}
    >
        {children}
    </div>
);

const OtcReviewSummary = ({
    youSendText,
    displayAmount,
    feeText,
    backendFee,
    recipientDetails,
    currentRate,
    preferredRate,
    usePreferredRate,
    fromCurrency,
    toCurrency,
    notes,
    displayMemo,
    recipientSkipped,
    onAddRecipient,
    onAddMemo,
}) => {
    const destinationCurrencySymbol = currencyPairCheck(toCurrency);
    const recipientAmount = Number(displayAmount || 0) * Number(currentRate || 0);
    const totalToDeduct = recipientAmount + Number(backendFee || 0);
    const hasPreferredRate = Boolean(usePreferredRate && Number(preferredRate) > 0);
    const accountNumber = recipientDetails?.accountNumber || recipientDetails?.extra?.iban || "";

    return (
        <div className="overflow-hidden rounded-xl bg-white">
            <div className="space-y-6 p-5 sm:p-6">
                <OtcValueRow label="You send">
                    <span className="font-semibold text-[#101828]">{youSendText}</span>
                </OtcValueRow>

                {hasPreferredRate && (
                    <OtcValueRow label="Preferred rate">
                        {destinationCurrencySymbol}
                        {formatRate(preferredRate)} / {fromCurrency}
                    </OtcValueRow>
                )}

                <OtcValueRow label="Our current rate">
                    {destinationCurrencySymbol}
                    {formatRate(currentRate)} / {fromCurrency}
                </OtcValueRow>

                {!recipientSkipped && (
                    <OtcValueRow label="Recipient to receive">
                        {destinationCurrencySymbol}
                        {recipientAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </OtcValueRow>
                )}

                <OtcValueRow label="Transfer fee">{feeText}</OtcValueRow>

                {!!notes && <OtcValueRow label="Notes">{notes}</OtcValueRow>}

                {recipientSkipped ? (
                    <DashedSection>
                        <OtcValueRow label={`Recipient to receive @${formatRate(currentRate)}`}>
                            {destinationCurrencySymbol}
                            {recipientAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </OtcValueRow>
                    </DashedSection>
                ) : (
                    <DashedSection>
                        <OtcValueRow label="Total to deduct">
                            {destinationCurrencySymbol}
                            {totalToDeduct.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </OtcValueRow>
                    </DashedSection>
                )}

                <OtcValueRow label="Send to">
                    {recipientSkipped ? (
                        <DeferredDetail onClick={onAddRecipient} />
                    ) : (
                        <div className="space-y-1">
                            {!!recipientDetails?.name && (
                                <p className="font-medium text-[#45556C]">
                                    {recipientDetails.name}
                                </p>
                            )}
                            {!!recipientDetails?.bankName && <p>{recipientDetails.bankName}</p>}
                            {!!accountNumber && <p>{accountNumber}</p>}
                            {!!recipientDetails?.extra?.routingNumber && (
                                <p>{recipientDetails.extra.routingNumber}</p>
                            )}
                        </div>
                    )}
                </OtcValueRow>

                <DashedSection bottom={false}>
                    <OtcValueRow label="Memo">
                        {recipientSkipped && !displayMemo ? (
                            <DeferredDetail onClick={onAddMemo} />
                        ) : (
                            displayMemo || "N/A"
                        )}
                    </OtcValueRow>
                </DashedSection>
            </div>

            <div className="flex items-start gap-2 bg-[#EDF3FF] px-5 py-3 text-xs text-[#344B80] sm:px-6">
                <InfoCircle className="mt-0.5 shrink-0" size={16} color="#2C53F2" variant="Bold" />
                <p>
                    {recipientSkipped
                        ? "If approved, you’ll need to fund your wallet and submit the required recipient details."
                        : "If approved, you’ll need to fund your wallet to complete this transfer."}
                </p>
            </div>
        </div>
    );
};

export default function ReviewDetailsCard({
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    isBankRecipient,
    currency,
    youSendText,
    displayAmount,
    feeText,
    recipientDetails,
    crossBorderRate,
    crossBorderPreferredRate,
    crossBorderUsePreferredRate,
    crossBorderIsOtc,
    crossBorderFromCurrency,
    crossBorderOtcNotes,
    crossBorderRecipientSkipped,
    backendFee,
    onAddRecipient,
    onAddMemo,
    cbTo,
    usdReviewDetails,
    displayMemo,
    displayInvoiceName,
    displayOtherDocName,
}) {
    const preferredRate = Number(crossBorderPreferredRate) || 0;
    const currentRate = Number(crossBorderRate) || 0;

    if (isCrossBorder && crossBorderIsOtc) {
        return (
            <OtcReviewSummary
                youSendText={youSendText}
                displayAmount={displayAmount}
                feeText={feeText}
                backendFee={backendFee}
                recipientDetails={recipientDetails}
                currentRate={currentRate}
                preferredRate={preferredRate}
                usePreferredRate={crossBorderUsePreferredRate}
                fromCurrency={crossBorderFromCurrency}
                toCurrency={cbTo}
                notes={crossBorderOtcNotes}
                displayMemo={displayMemo}
                recipientSkipped={crossBorderRecipientSkipped}
                onAddRecipient={onAddRecipient}
                onAddMemo={onAddMemo}
            />
        );
    }

    return (
        <div className="bg-white rounded-xl p-6 space-y-6 text-normal">
            <div className="flex justify-between">
                <span className="text-[#344054]">You send</span>
                <span className="text-[#101828] font-semibold text-base">{youSendText}</span>
            </div>

            <div className="flex justify-between">
                <span className="text-[#344054]">Transfer fee</span>
                <span className="text-[#667085] font-medium">{feeText}</span>
            </div>

            {isCrossBorder && (
                <div className="flex justify-between">
                    <span className="text-[#344054]">Recipient recieves</span>
                    <span className="text-[#101828] font-semibold">
                        {recipientDetails?.payoutCurrency}{" "}
                        {Number(Number(displayAmount) * crossBorderRate)?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </span>
                </div>
            )}

            <hr className="border-t border-[#E4E7EC]" />

            <div className="flex justify-between">
                <span className="text-[#344054] block mb-1">Send to</span>
                <div className="text-right">
                    {!!(recipientDetails?.name || isCryptoFlow) && (
                        <p className="text-[#101828] font-semibold">
                            {recipientDetails?.name || "External wallet"}
                        </p>
                    )}

                    {!isCryptoFlow &&
                        !isCryptoToFiatFlow &&
                        !isCrossBorder &&
                        !!recipientDetails?.accountNumber && (
                            <p className="text-[#667085]">{recipientDetails?.accountNumber}</p>
                        )}

                    {isCryptoFlow && (
                        <>
                            {!!recipientDetails?.accountNumber && (
                                <p className="text-[#667085] break-all">
                                    {recipientDetails?.accountNumber}
                                </p>
                            )}
                            {recipientDetails?.network && (
                                <p className="text-[#667085]">
                                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-[#F2F3FC] text-[#383B56]">
                                        {recipientDetails.network}
                                    </span>
                                </p>
                            )}
                        </>
                    )}

                    {isCryptoToFiatFlow && (
                        <>
                            {!!recipientDetails?.bankName && (
                                <p className="text-[#667085]">{recipientDetails?.bankName}</p>
                            )}
                            {!!recipientDetails?.accountNumber && (
                                <p className="text-[#667085]">{recipientDetails?.accountNumber}</p>
                            )}
                            {!!recipientDetails?.bankCode && (
                                <p className="text-[#667085]">{recipientDetails?.bankCode}</p>
                            )}
                            {!!recipientDetails?.payoutCurrency && (
                                <p className="text-[#667085]">
                                    Payout currency: {recipientDetails?.payoutCurrency}
                                </p>
                            )}
                            {recipientDetails?.network && (
                                <p className="text-[#667085]">
                                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-[#F2F3FC] text-[#383B56]">
                                        {recipientDetails.network}
                                    </span>
                                </p>
                            )}
                        </>
                    )}

                    {isCrossBorder && (
                        <>
                            {!!recipientDetails?.bankName && (
                                <p className="text-[#667085]">{recipientDetails?.bankName}</p>
                            )}
                            {!!(recipientDetails?.accountNumber || recipientDetails?.extra?.iban) && (
                                <p className="text-[#667085]">
                                    {recipientDetails?.accountNumber || recipientDetails?.extra?.iban}
                                </p>
                            )}
                            {recipientDetails?.extra?.iban && (
                                <p className="text-[#667085]">IBAN: {recipientDetails.extra.iban}</p>
                            )}
                            {recipientDetails?.extra?.swiftBic && (
                                <p className="text-[#667085]">
                                    SWIFT/BIC: {recipientDetails.extra.swiftBic}
                                </p>
                            )}
                            {recipientDetails?.extra?.routingNumber && (
                                <p className="text-[#667085]">
                                    Routing: {recipientDetails.extra.routingNumber}
                                </p>
                            )}
                            {recipientDetails?.extra?.bankCode && (
                                <p className="text-[#667085]">{recipientDetails.extra.bankCode}</p>
                            )}
                            {cbTo === "GBP" && recipientDetails?.extra?.sortCode && (
                                <p className="text-[#667085]">
                                    Sort code: {recipientDetails.extra.sortCode}
                                </p>
                            )}
                            {!!recipientDetails?.payoutCurrency && (
                                <p className="text-[#667085]">
                                    Payout currency: {recipientDetails?.payoutCurrency}
                                </p>
                            )}
                        </>
                    )}

                    {isBankRecipient && currency === "USD" && (
                        <>
                            {!!usdReviewDetails?.bankName && (
                                <p className="text-[#667085]">{usdReviewDetails?.bankName}</p>
                            )}
                            {!!usdReviewDetails?.ownerName && (
                                <p className="text-[#667085]">
                                    Account owner: {usdReviewDetails.ownerName}
                                </p>
                            )}

                            {usdReviewDetails?.accountType === "iban" ? (
                                <>
                                    {!!usdReviewDetails?.iban && (
                                        <p className="text-[#667085]">IBAN: {usdReviewDetails?.iban}</p>
                                    )}
                                    {!!usdReviewDetails?.bic && (
                                        <p className="text-[#667085]">
                                            SWIFT/BIC: {usdReviewDetails?.bic}
                                        </p>
                                    )}
                                    {!!usdReviewDetails?.country && (
                                        <p className="text-[#667085]">
                                            Country: {usdReviewDetails?.country}
                                        </p>
                                    )}
                                </>
                            ) : usdReviewDetails?.accountType === "international_wire" ? (
                                <>
                                    {!!usdReviewDetails?.accountNumber && (
                                        <p className="text-[#667085]">
                                            Account: {usdReviewDetails?.accountNumber}
                                        </p>
                                    )}
                                    {!!usdReviewDetails?.routingNumber && (
                                        <p className="text-[#667085]">
                                            Routing: {usdReviewDetails?.routingNumber}
                                        </p>
                                    )}
                                    {!!usdReviewDetails?.bic && (
                                        <p className="text-[#667085]">
                                            SWIFT/BIC: {usdReviewDetails?.bic}
                                        </p>
                                    )}
                                    {!!usdReviewDetails?.country && (
                                        <p className="text-[#667085]">
                                            Country: {usdReviewDetails?.country}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    {!!usdReviewDetails?.routingNumber && (
                                        <p className="text-[#667085]">
                                            Routing: {usdReviewDetails?.routingNumber}
                                        </p>
                                    )}
                                    {!!usdReviewDetails?.accountNumber && (
                                        <p className="text-[#667085]">
                                            Account: {usdReviewDetails?.accountNumber}
                                            {usdReviewDetails?.checkingOrSavings
                                                ? ` (${usdReviewDetails?.checkingOrSavings})`
                                                : ""}
                                        </p>
                                    )}
                                </>
                            )}

                            {!!usdReviewDetails?.fullAddress && (
                                <p className="text-[#667085]">{usdReviewDetails?.fullAddress}</p>
                            )}
                        </>
                    )}
                </div>
            </div>

            <hr className="border-t border-[#E4E7EC]" />

            <div className="flex justify-between">
                <span className="text-[#344054] block mb-1">Memo</span>
                <p className="text-[#667085] md:pl-80 text-end">{displayMemo || "N/A"}</p>
            </div>

            <hr className="border-t border-[#E4E7EC]" />

            <div className="flex justify-between">
                <span className="text-[#344054]">Invoice</span>
                <span className="text-[#667085]">{displayInvoiceName}</span>
            </div>
            <div className="flex justify-between">
                <span className="text-[#344054]">Supporting document</span>
                <span className="text-[#667085]">{displayOtherDocName}</span>
            </div>
        </div>
    );
}
